import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { Users, TrendingUp, Brain, Banknote, Lightbulb, MapPin, GraduationCap, Briefcase } from "lucide-react";

const COLORS = ["#1E3A8A", "#F59E0B", "#E11D48", "#10B981", "#0EA5E9", "#8B5CF6", "#F43F5E"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-10 text-gray-500">Loading dashboard…</div>;
  if (!stats) return <div className="p-10 text-gray-500">No data</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#E11D48] font-semibold">Pithapuram Constituency</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl mt-1">Skilling Census Dashboard</h1>
          <p className="text-gray-600 mt-1 text-sm">Live, day-to-day youth registrations & empowerment metrics.</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-gray-100 rounded-full px-4 py-2 shadow-sm">
          <div className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
          <span className="text-sm font-medium">Live · Updated just now</span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <Kpi color="#1E3A8A" icon={<Users />} label="Total Registered" value={stats.total} sub="Pithapuram youth" />
        <Kpi color="#F59E0B" icon={<TrendingUp />} label="Today" value={stats.today_count} sub="New registrations" />
        <Kpi color="#E11D48" icon={<Brain />} label="AI Upskill" value={stats.ai_upskill_count} sub="Interested" />
        <Kpi color="#10B981" icon={<Lightbulb />} label="Startup Ideas" value={stats.startup_count} sub="Ready to incubate" />
        <Kpi color="#0EA5E9" icon={<Banknote />} label="Bank Linkage" value={stats.bank_linkage_count} sub="Need loan support" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Trend chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Last 14 days</div>
              <h3 className="font-heading font-bold text-lg">Daily registrations trend</h3>
            </div>
            <TrendingUp className="w-5 h-5 text-[#1E3A8A]" />
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={stats.trend_14d}>
              <defs>
                <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E3A8A" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} />
              <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }} />
              <Line type="monotone" dataKey="count" stroke="#1E3A8A" strokeWidth={3} dot={{ fill: "#F59E0B", r: 4 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Gender donut */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <h3 className="font-heading font-bold text-lg mb-4">Gender split</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={stats.by_gender} dataKey="count" nameKey="name" innerRadius={60} outerRadius={90} paddingAngle={3}>
                {stats.by_gender.map((g, i) => <Cell key={`gender-${g.name}`} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Mandal breakdown - new single source of truth display */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-lg">By mandal</h3>
              <p className="text-xs text-gray-500">Constituency rollup — sum equals total registrations</p>
            </div>
            <MapPin className="w-5 h-5 text-[#1E3A8A]" />
          </div>
          {stats.by_mandal && (
            <div className="space-y-3">
              {stats.by_mandal.map((m, i) => {
                const total = stats.by_mandal.reduce((s, x) => s + x.count, 0) || 1;
                const pct = (m.count / total) * 100;
                const color = COLORS[i % COLORS.length];
                return (
                  <div key={m.name} data-testid={`mandal-row-${m.name}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{m.name}</span>
                      <span><span className="font-heading font-bold" style={{ color }}>{m.count}</span> <span className="text-gray-400 text-xs">({pct.toFixed(0)}%)</span></span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                  </div>
                );
              })}
              <div className="pt-2 mt-2 border-t border-dashed border-gray-200 text-xs text-gray-500 flex justify-between">
                <span>Total across mandals</span>
                <span className="font-heading font-bold text-gray-700">
                  {stats.by_mandal.reduce((s, x) => s + x.count, 0)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Villages */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Top villages</h3>
            <MapPin className="w-5 h-5 text-[#E11D48]" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.by_village.slice(0, 10)} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" stroke="#9ca3af" fontSize={12} allowDecimals={false} />
              <YAxis type="category" dataKey="name" stroke="#9ca3af" fontSize={11} width={100} />
              <Tooltip />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                {stats.by_village.slice(0, 10).map((v, i) => <Cell key={`vill-${v.name}`} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Domains */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Top interested domains</h3>
            <Brain className="w-5 h-5 text-[#F59E0B]" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.by_domain}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" stroke="#9ca3af" fontSize={10} angle={-25} textAnchor="end" height={70} interval={0} />
              <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {stats.by_domain.map((d, i) => <Cell key={`dom-${d.name}`} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Education level</h3>
            <GraduationCap className="w-5 h-5 text-[#10B981]" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={stats.by_education} dataKey="count" nameKey="name" outerRadius={90} label={({ name }) => name}>
                {stats.by_education.map((e, i) => <Cell key={`edu-${e.name}`} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">Employment status</h3>
            <Briefcase className="w-5 h-5 text-[#0EA5E9]" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.by_employment}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" stroke="#9ca3af" fontSize={11} />
              <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[6, 6, 0, 0]} fill="#0EA5E9" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-lg">What they want</h3>
            <Lightbulb className="w-5 h-5 text-[#E11D48]" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={stats.by_preference} dataKey="count" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={3}>
                {stats.by_preference.map((p, i) => <Cell key={`pref-${p.name}`} fill={COLORS[(i + 2) % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top SSC AI-Upskill Courses */}
      {stats.by_course && stats.by_course.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] mt-6" data-testid="dashboard-top-courses">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-lg">Top AI-Upskill SSC courses picked</h3>
              <p className="text-xs text-gray-500">Most-selected NSDC Sector Skill Council courses with AI integration</p>
            </div>
            <Brain className="w-5 h-5 text-[#1E3A8A]" />
          </div>
          <div className="space-y-3">
            {stats.by_course.map((c, i) => {
              const max = Math.max(...stats.by_course.map((x) => x.count));
              const pct = (c.count / max) * 100;
              const color = COLORS[i % COLORS.length];
              return (
                <div key={`${c.ssc}-${c.name}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium flex items-center gap-2">
                      {c.name}
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" style={{ backgroundColor: `${color}15`, color }}>{c.ssc}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E] font-bold uppercase tracking-wider">AI</span>
                    </span>
                    <span className="text-gray-500">{c.count}</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ color, icon, label, value, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-md transition-all" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: color }}>
          {icon}
        </div>
      </div>
      <div className="font-heading font-bold text-3xl tracking-tighter">{value}</div>
      <div className="text-sm font-medium text-gray-700 mt-0.5">{label}</div>
      <div className="text-xs text-gray-500">{sub}</div>
    </div>
  );
}
